import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Add, Remove } from '@mui/icons-material';
import { Nullable, QueryListResponse } from 'global';
import { BpfLinkType } from '@trnsact/trnsact-shared-types';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { Box, Button, IconButton, Stack, Typography, Tooltip } from '@mui/material';

import { ActionsProps } from './types';
import { StageNotes } from '../stagesNotes';
import { CompletedBadge } from './CompletedBadge';
import { bpfAPI, ViewStage } from '../../api';
import { calcFieldsAmount, checkIsStageFieldsExist } from '../../lib';
import { BPFAPINameSpase, BpfCloseReasonType, StageNote } from '../../types';
import { CloseDealWithReason } from '../closeReason/closeVO/CloseDealWithReason';
import { StageAttributesContainer } from './attributes/StageAttributesContainer';

interface Props extends ActionsProps {
  parentId: string;
  isDealClosed: boolean;
  parentType: BpfLinkType;
  canReopenStage: boolean;
  isStagesUpdating: boolean;
  stage: Nullable<ViewStage>;
  isStageCompleting: boolean;
  isStageUnCompleting: boolean;
  isCompleteDisabled: boolean;
  closeReasonId: Nullable<string>;
  isStageLastForDepartment: boolean;
  closeFlow: Nullable<BpfCloseReasonType>;
  isOtherDepartmentsHaveUncompletedStages?: { hasUncompleted: boolean; departmentNames: string[] };
}

export const StageView = ({
  stage,
  actions,
  parentId,
  closeFlow,
  parentType,
  isDealClosed,
  closeReasonId,
  canReopenStage,
  isStagesUpdating,
  isStageCompleting,
  isStageUnCompleting,
  isCompleteDisabled,
  isStageLastForDepartment,
  isOtherDepartmentsHaveUncompletedStages = { hasUncompleted: false, departmentNames: [] },
}: Props) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const { data, loading: isNotesLoading } = useQuery<BPFAPINameSpase<QueryListResponse<StageNote>>>(
    bpfAPI.getStageNotes,
    {
      notifyOnNetworkStatusChange: true,
      skip: !stage?.businessProcessFlowStageId,
      variables: { input: { parentId, parentType, stageId: stage?.businessProcessFlowStageId! } },
    }
  );

  if (!stage) return null;

  const { requiredFields, requiredCompletedFields } = calcFieldsAmount(
    stage.fieldValues?.values?.length ? stage.fieldValues.values : stage.stageFields
  );

  return (
    <>
      <Box className="currentStages">
        <Stack direction="row" alignItems="center">
          <IconButton size="small" onClick={() => setIsCollapsed(prevState => !prevState)}>
            {!isCollapsed ? <Remove fontSize="small" /> : <Add fontSize="small" />}
          </IconButton>

          <Typography variant="subtitle2" component="span">
            {stage.name}
          </Typography>
        </Stack>

        <Stack direction="row" gap="1rem">
          {!isDealClosed && (
            <Button size="small" color="error" onClick={() => actions.onChangeCloseFlow(BpfCloseReasonType.Lost)}>
              Close Deal
            </Button>
          )}

          <RenderWithCondition condition={!isDealClosed && isStageLastForDepartment}>
            <Tooltip
              title={requiredFields !== requiredCompletedFields ? 'Finish Stages process before Complete deal' : ''}
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={stage.isLastStage && !stage?.fieldValues?.isCompleted}
                  onClick={() => actions.onChangeCloseFlow(BpfCloseReasonType.Won)}
                >
                  Complete Deal
                </Button>
              </span>
            </Tooltip>
          </RenderWithCondition>

          <RenderWithCondition
            condition={!stage?.fieldValues?.isCompleted}
            fallBack={
              <Stack direction="row" gap="0.5rem" alignItems="center">
                <CompletedBadge stage={stage} />

                <RenderWithCondition condition={canReopenStage}>
                  <Button
                    size="small"
                    variant="outlined"
                    loading={isStageUnCompleting}
                    onClick={() => actions.onUnCompleteStage(stage.businessProcessFlowStageId, parentId, parentType)}
                  >
                    Reopen stage
                  </Button>
                </RenderWithCondition>
              </Stack>
            }
          >
            <Tooltip
              title={
                requiredFields !== requiredCompletedFields ? 'Complete Required Attributes before Complete Stage' : ''
              }
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  loading={isStageCompleting}
                  disabled={isCompleteDisabled || requiredFields !== requiredCompletedFields}
                  onClick={() => actions.onCompleteStage(stage.businessProcessFlowStageId, parentId, parentType)}
                >
                  Complete
                </Button>
              </span>
            </Tooltip>
          </RenderWithCondition>
        </Stack>
      </Box>

      {!isCollapsed && (
        <RenderWithCondition condition={checkIsStageFieldsExist(stage)}>
          <Box className="attributes">
            <StageNotes
              parentId={parentId}
              parentType={parentType}
              loading={isNotesLoading}
              key={JSON.stringify({ isNotesLoading })}
              apiNotes={data?.bpf?.result?.list ?? []}
              stageId={stage.businessProcessFlowStageId}
            />

            <StageAttributesContainer
              stage={stage}
              parentId={parentId}
              parentType={parentType}
              isLoading={isStagesUpdating}
              onUpdateStageAttributes={actions.onUpdateStageAttributes}
              closeSlot={
                <RenderWithCondition
                  condition={(closeFlow === 'WON' && isStageLastForDepartment) || closeFlow === 'LOST'}
                >
                  <CloseDealWithReason
                    type={closeFlow!}
                    parentId={parentId}
                    parentType={parentType}
                    isDealClosed={isDealClosed}
                    closeReasonId={closeReasonId}
                    templateId={stage.businessProcessFlowId}
                    stageId={stage.businessProcessFlowStageId}
                    onCancelCloseFlow={() => actions.onChangeCloseFlow(null)}
                    isOtherDepartmentsHaveUncompletedStages={isOtherDepartmentsHaveUncompletedStages}
                  />
                </RenderWithCondition>
              }
            />
          </Box>
        </RenderWithCondition>
      )}
    </>
  );
};
